// components/layout/Sidebar.tsx
"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Tag,
  Headphones,
  Store,
  Settings,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const MENU_ITEMS = [
  { href: "/", label: "داشبورد", icon: LayoutDashboard },
  { href: "/products", label: "محصولات", icon: Package },
  { href: "/orders", label: "سفارشات", icon: ShoppingCart },
  { href: "/users", label: "کاربران", icon: Users },
  { href: "/coupons", label: "کدهای تخفیف", icon: Tag },
  { href: "/support", label: "پشتیبانی", icon: Headphones },
  { href: "/pos", label: "صندوق‌ها", icon: Store },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const pathname = usePathname();

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // استایل دکمه فعال منو (برگرفته از کلاس active در قالب شما)
  const activeItemCls =
    "bg-[linear-gradient(90deg,rgba(139,195,74,0.22),rgba(139,195,74,0.05))] text-white border-r-[3px] border-[#8bc34a] font-semibold";

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300",
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={cn(
          "fixed lg:static inset-y-0 right-0 z-50 w-64 flex flex-col shrink-0 transition-transform duration-300 ease-in-out",
          // پس‌زمینه با گرادیان عمودی و سایه تیره (دقیقا مطابق فایل ارسالی)
          "bg-[linear-gradient(180deg,#06371f_0%,#084d2b_55%,#0a5a32_100%)] text-white shadow-[-4px_0_24px_-4px_rgba(8,60,32,0.2)]",
          isOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"
        )}
      >
        <div className="p-4 lg:p-6 flex items-center justify-between h-16 mb-4">
          <div className="flex items-center gap-2.5">
            {/* لوگو با استایل brand-mark */}
            <div className="w-10 h-10 rounded-xl bg-[linear-gradient(135deg,#a5d76e,#5aa62e)] flex items-center justify-center font-black text-xl shrink-0 text-[#06371f] shadow-[0_6px_16px_rgba(139,195,74,0.35)]">
              پ
            </div>
            <div className="flex flex-col">
              <h2 className="text-sm lg:text-[15px] font-bold text-white leading-tight">
                مدیریت عملیات
              </h2>
              <span className="text-[10px] text-[#a9c9b5] mt-0.5">
                پلتفرم مدیریت و توسعه
              </span>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-white/70 hover:text-white hover:bg-white/10"
            onClick={onClose}
            aria-label="بستن منو"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="px-5 mb-2">
          <span className="text-[10px] text-[#8fb89d] tracking-wider">فضای کاری</span>
        </div>

        <nav className="flex-1 px-3 space-y-1.5 overflow-y-auto">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={onClose}
                className={cn(
                  "relative flex items-center gap-3 py-2.5 pr-4 pl-4 text-[12.5px] transition-all group rounded-xl",
                  isActive
                    ? activeItemCls
                    : "text-[#c7dccd] hover:bg-white/5 hover:text-white"
                )}
              >
                <Icon
                  className={cn(
                    "h-[18px] w-[18px] shrink-0 transition-colors",
                    isActive
                      ? "text-[#8bc34a]"
                      : "text-inherit group-hover:text-white"
                  )}
                  strokeWidth={2}
                />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 mt-auto">
          <Link
            href="/settings"
            onClick={onClose}
            className={cn(
              "relative flex items-center gap-3 py-2.5 pr-4 pl-4 text-[12.5px] transition-all group rounded-xl",
              pathname === "/settings"
                ? activeItemCls
                : "text-[#c7dccd] hover:bg-white/5 hover:text-white"
            )}
          >
            <Settings
              className={cn(
                "h-[18px] w-[18px] shrink-0 transition-colors",
                pathname === "/settings"
                  ? "text-[#8bc34a]"
                  : "text-inherit group-hover:text-white"
              )}
              strokeWidth={2}
            />
            تنظیمات
          </Link>
        </div>
      </aside>
    </>
  );
}